import { FC, useEffect, useRef } from 'react';
// Hooks
import useNavigateTo from 'hooks/useNavigateTo';
// Redux
import { useAppDispatch, useAppSelector } from 'hooks/useStore';
import { setQuestionReady, resetQuestionStatuses, setQuestionIndex, setQuestionStart } from 'store/player/playerSlice';
import { selectQuestion, selectQuestionsLength, selectQuiz } from 'store/quiz/quizSelectors';
import { selectQuestionIndex, selectQuestionEnd, selectFilteredAnswersOptions } from 'store/player/playerSelectors';
import QuizTypes from 'types/QuizTypes';
import QuestionTypes from 'types/QuestionTypes';
import Paths from 'types/Paths';
import IQuiz from 'models/Quiz';
// Components
import AppBar from 'components/AppBar';
import PoweredBy from 'components/PoweredBy';
import ConnectionsGrid from './ConnectionsGrid';
import SimpleQuestion from './SimpleQuestion';
import TopList from './TopList';
import ImagePin from './ImagePin';

const QuestionPage:FC = () => {
  const dispatch = useAppDispatch();
  // State
  const quiz:IQuiz = useAppSelector(selectQuiz);
  const question = useAppSelector(selectQuestion);
  const questionIndex = useAppSelector(selectQuestionIndex);
  const questionsLength = useAppSelector(selectQuestionsLength);
  const questionEnd:boolean = useAppSelector(selectQuestionEnd);
  const filteredAnswersOptions = useAppSelector(selectFilteredAnswersOptions);

  const navigateTo = useNavigateTo();

  const timeout = useRef<any>(null);

  const timeoutTime = (quiz.questionsSettings.delay || 2) * 1000;

  const scoreVisible = [QuizTypes.BeatTheExpert, QuizTypes.Scoring, QuizTypes.Streak].includes(quiz.type);
  const questionNumberVisible = [QuestionTypes.ImagePin].includes(question.type) || [QuizTypes.BeatTheExpert].includes(quiz.type);

  useEffect(() => {
    if (quiz.type === QuizTypes.RankingPoll && !filteredAnswersOptions.length) {
      navigateTo('register');
      return;
    }
    
    if (quiz.type === QuizTypes.BeatTheExpert) {
      dispatch(setQuestionReady(true));
      dispatch(setQuestionStart(true));
    }

    if (!question.image && !question.video && !question.audio) dispatch(setQuestionReady(true));

    return () => {
      if ( timeout.current ) clearTimeout(timeout.current);
    }
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    if ( questionEnd ) {
      const isLastQuestion = questionsLength === questionIndex + 1;
      const config = quizNavigationConfig[quiz.type];
      const nextPage = config ? (isLastQuestion ? config.lastQuestion : config.default) : 'pages-handler';
      timeout.current = setTimeout(() => {
        dispatch(resetQuestionStatuses());
        dispatch(setQuestionIndex(questionIndex + 1));
        navigateTo(nextPage);
      }, timeoutTime);
    }
    // eslint-disable-next-line
  }, [questionEnd]);

  const renderQuestion = () => {
    switch (question.type) {
      case QuestionTypes.ConnectionGrid:
        return <ConnectionsGrid />;
      case QuestionTypes.TopList:
        return <TopList />;
      case QuestionTypes.ImagePin:
        return <ImagePin />;
      default:
        return <SimpleQuestion />;
    }
  };

  return (
    <div
      className="flex-grow flex flex-col"
      style={{ background: quiz.backgroundImage ? `url('${quiz.backgroundImage}') center / cover` : '' }}
    >
      <div className="p-4">
        <AppBar scoreVisible={scoreVisible} questionNumberVisible={questionNumberVisible} />
      </div>
      {renderQuestion()}
      <PoweredBy />
    </div>
  )
}

export default QuestionPage;

const quizNavigationConfig:Partial<Record<QuizTypes, { lastQuestion: Paths; default: Paths }>> = {
  [QuizTypes.BeatTheExpert]: {
    lastQuestion: 'beat-the-expert-result',
    default: 'pages-handler',
  },
  [QuizTypes.Scoring]: {
    lastQuestion: 'scoring-result',
    default: 'pages-handler',
  },
  [QuizTypes.Streak]: {
    lastQuestion: 'scoring-result',
    default: 'streak-handler',
  },
};
